import {
	RoomAudioRenderer,
	RoomContext,
	useRoomContext,
} from "@livekit/components-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Skeleton } from "@/components/ui/skeleton";
import { useMediaStatusSync } from "@/hooks/use-media-status-sync";
import { useParticipantSounds } from "@/hooks/use-participant-sounds";
import { useStageModerator } from "@/hooks/use-stage";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import "@livekit/components-styles";
import {
	Columns2,
	LayoutDashboard,
	Loader2,
	Mic,
	PictureInPicture2,
	Radio,
	Rows2,
	Square,
	Video,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { BroadcastTour, VoiceTour } from "./app-tour";
import { BackgroundEffectApplier } from "./background-selector";
import {
	BroadcastReactionOverlay,
	BroadcastReactions,
} from "./broadcast-reactions";
import { BroadcastSpeakerControlBar } from "./broadcast-speaker-control-bar";
import { BroadcastVideoGrid } from "./broadcast-video-grid";
import { CallVideoGrid } from "./call-video-grid";
import { CustomControlBar as ControlBar } from "./custom-control-bar";
import { ParticipantList } from "./participant-list";
import { RoomOverlay } from "./room-overlay";
import { SpotlightOverlay } from "./spotlight-overlay";
import { StageControlBar } from "./stage-control-bar";
import { StageRequestQueue } from "./stage-request-queue";
import { StageVideoGrid } from "./stage-video-grid";
import { VolumeControl } from "./volume-control";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Unified component handling voice-only, broadcast-only, and combined views
export function CombinedVoiceBroadcastView() {
	const user = useStore(authStore, (state) => state.user);
	const activeChannel = useStore(uiStore, (state) => state.activeChannel);
	const channels = useStore(coreStore, (state) => state.channels);
	const room = useStore(uiStore, (state) => state.room);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const broadcastRoom = useStore(uiStore, (state) => state.broadcastRoom);
	const volume = useStore(uiStore, (state) => state.broadcastVolume);
	const activeBroadcastViewer = useStore(
		uiStore,
		(state) => state.activeBroadcastViewer
	);
	const handleJoinBroadcast = useStore(
		uiStore,
		(state) => state.handleJoinBroadcast
	);

	const pipOpenFn = useStore(uiStore, (state) => state.pipOpenFn);
	const layoutMode = useStore(uiStore, (state) => state.combinedLayoutMode);
	const setLayoutMode = useStore(
		uiStore,
		(state) => state.setCombinedLayoutMode
	);
	const isBroadcastAudioActive = useStore(
		uiStore,
		(state) => state.isBroadcastAudioActive
	);

	const [isJoiningAsSpeaker, setIsJoiningAsSpeaker] = useState(false);
	const isHandlingRevocationRef = useRef(false);

	useMediaStatusSync();

	// Satisfy browser autoplay policy for broadcast audio
	useEffect(() => {
		if (!broadcastRoom) return;
		broadcastRoom.startAudio().catch(() => {});
	}, [broadcastRoom]);

	const isInvitedSpeaker =
		user?.uid && broadcast?.invitedSpeakers?.includes(user.uid);
	const canPublish = activeBroadcastViewer?.canPublish ?? false;

	const hasVoiceCall = activeChannel && room;
	const isConnectingToVoice = activeChannel && !room;
	const hasBroadcast =
		broadcast &&
		broadcastRoom &&
		(broadcast.status === "live" || broadcast.status === "paused");

	const channelData = activeChannel ? channels.get(activeChannel.id) : null;
	const isStageChannel = channelData?.type === "stage";
	const isStageModerator = useStageModerator(activeChannel?.id ?? "");

	useEffect(() => {
		if (isInvitedSpeaker && !canPublish) {
			toast.info(
				"You've been invited to speak! Click 'Join as Speaker' to participate.",
				{ id: "speaker-invitation", duration: Number.POSITIVE_INFINITY }
			);
		} else {
			toast.dismiss("speaker-invitation");
		}
	}, [isInvitedSpeaker, canPublish]);

	useEffect(() => {
		const handleRevocation = async () => {
			if (
				!isInvitedSpeaker &&
				canPublish &&
				broadcastRoom &&
				!isHandlingRevocationRef.current
			) {
				isHandlingRevocationRef.current = true;
				toast.info(
					"Your speaker permission has been removed. Returning to viewer mode."
				);

				const localTracks = Array.from(
					broadcastRoom.localParticipant.trackPublications.values()
				);
				for (const publication of localTracks) {
					if (publication.track) {
						await broadcastRoom.localParticipant.unpublishTrack(
							publication.track
						);
					}
				}

				uiStore.getState().setActiveBroadcastViewer({
					token: activeBroadcastViewer?.token ?? "",
					canPublish: false,
				});

				isHandlingRevocationRef.current = false;
			}
		};
		handleRevocation();
	}, [
		isInvitedSpeaker,
		canPublish,
		broadcastRoom,
		activeBroadcastViewer?.token,
	]);

	const handleJoinAsSpeaker = useCallback(async () => {
		setIsJoiningAsSpeaker(true);
		try {
			if (room) {
				for (const publication of room.localParticipant.trackPublications.values()) {
					publication.track?.stop();
				}
				await room.disconnect();
				uiStore.getState().setRoom(null);
				uiStore.getState().setActiveChannel(null);
			}
			if (broadcastRoom) {
				await broadcastRoom.disconnect();
				uiStore.getState().setBroadcastRoom(null);
			}
			await handleJoinBroadcast(true);
			toast.success(
				"Joined as speaker! You can now enable your mic and camera."
			);
		} catch (error) {
			console.error("Failed to join as speaker:", error);
			toast.error("Failed to join as speaker");
		} finally {
			setIsJoiningAsSpeaker(false);
		}
	}, [room, broadcastRoom, handleJoinBroadcast]);

	const handlePopOutToPip = async () => {
		if (!pipOpenFn) return;

		const success = await pipOpenFn();
		if (!success) return;

		const currentGridItems = uiStore.getState().gridItems;
		const firstItem = currentGridItems[0];
		if (!firstItem) return;

		const currentIndex = firstItem.activeTabIndex;
		const otherTabIndex = firstItem.tabs.findIndex(
			(tab, i) =>
				i !== currentIndex &&
				tab.type !== "voiceChannel" &&
				tab.type !== "broadcastControls"
		);

		if (otherTabIndex !== -1) {
			// Switch to another existing tab, keep voiceChannel tab in the bar
			uiStore.getState().setActiveTab(firstItem.id, otherTabIndex);
		} else {
			// Only voice or broadcast tabs are left, so remove them and empty the grid.
			uiStore.getState().closeTab(firstItem.id, currentIndex);
		}
	};

	const activeTab = useStore(uiStore, (state) => state.combinedActiveTab);
	const setActiveTab = useStore(uiStore, (state) => state.setCombinedActiveTab);

	const layoutOptions = useMemo(
		() => [
			{
				value: "split-vertical" as const,
				label: "Split Vertical",
				icon: Columns2,
				group: "split",
			},
			{
				value: "split-horizontal" as const,
				label: "Split Horizontal",
				icon: Rows2,
				group: "split",
			},
			{
				value: "tabs" as const,
				label: "Tabs",
				icon: Square,
				group: "single",
			},
		],
		[]
	);

	// Nothing active at all
	if (!(activeChannel || broadcast)) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-4">
				<Radio className="size-16 text-muted-foreground" />
				<h3 className="font-semibold text-lg">No Active Broadcast</h3>
				<p className="text-muted-foreground text-sm">
					There is no broadcast currently available.
				</p>
			</div>
		);
	}

	// Voice-only: connecting to voice channel (no broadcast)
	if (activeChannel && !room && !hasBroadcast) {
		return (
			<div className="flex h-full items-center justify-center bg-background">
				<Card className="flex flex-col items-center gap-4 border-none bg-transparent p-8 shadow-none">
					<Loader2 className="size-8 animate-spin text-primary" />
					<p className="text-muted-foreground text-sm">
						Connecting to voice channel...
					</p>
				</Card>
			</div>
		);
	}

	// Broadcast connecting: no broadcastRoom yet (with or without voice)
	if (broadcast && !broadcastRoom && !hasVoiceCall) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-4">
				<Radio className="size-16 text-muted-foreground" />
				<p className="text-muted-foreground">Connecting to broadcast...</p>
			</div>
		);
	}

	// Voice-only (no broadcast active)
	if (hasVoiceCall && !hasBroadcast) {
		return (
			<RoomContext.Provider value={room}>
				<div
					className="flex h-full flex-col bg-background"
					data-lk-theme="default"
				>
					<BackgroundEffectApplier />
					<div className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-4 max-md:h-16 max-md:px-14">
						<div className="flex items-center gap-3">
							<div className="flex size-8 items-center justify-center rounded-full bg-primary/10">
								<Video className="size-4 text-primary" />
							</div>
							<h2 className="font-semibold text-foreground">
								{activeChannel.name}
							</h2>
						</div>
						<div className="flex items-center gap-2">
							{isStageChannel && isStageModerator && (
								<StageRequestQueue channelId={activeChannel.id} />
							)}
							<Button
								data-tour="pip-button"
								onClick={handlePopOutToPip}
								size="sm"
								title="Pop out to Picture-in-Picture"
								variant="secondary"
							>
								<PictureInPicture2 className="size-4 text-foreground" />
							</Button>
							<ParticipantList />
						</div>
					</div>
					{isStageChannel ? (
						<StageVideoGrid channelId={activeChannel.id} variant="full" />
					) : (
						<CallVideoGrid variant="full" />
					)}
					<RoomAudioRenderer />
					<DisconnectHandler />
					<ParticipantSoundHandler />
					<RoomOverlay />
					{isStageChannel ? (
						<StageControlBar channelId={activeChannel.id} />
					) : (
						<ControlBar />
					)}
					<VoiceTour />
				</div>
			</RoomContext.Provider>
		);
	}

	// Broadcast-only (no voice call)
	if (!hasVoiceCall && hasBroadcast && broadcastRoom && broadcast) {
		return (
			<RoomContext.Provider value={broadcastRoom}>
				<div className="flex h-full flex-col" data-lk-theme="default">
					<div className="flex h-14 shrink-0 items-center justify-between border-b px-4 max-md:px-14">
						<div className="flex items-center gap-2">
							<Radio className="size-4 text-green-500" />
							<h2 className="font-semibold text-foreground">
								{broadcast.title}
							</h2>
							<span
								className={`rounded px-2 py-0.5 font-medium text-xs ${
									broadcast.status === "live"
										? "bg-green-500/20 text-green-500"
										: "bg-yellow-500/20 text-yellow-500"
								}`}
							>
								{broadcast.status.toUpperCase()}
							</span>
							{isConnectingToVoice && (
								<span className="ml-2 flex items-center gap-1.5 rounded bg-primary/10 px-2 py-0.5 text-primary text-xs">
									<Loader2 className="size-3 animate-spin" />
									<Video className="size-3" />
									Connecting to {activeChannel.name}...
								</span>
							)}
						</div>
						<div className="flex items-center gap-3">
							{isInvitedSpeaker && !canPublish && (
								<Button
									disabled={isJoiningAsSpeaker}
									onClick={handleJoinAsSpeaker}
									size="sm"
									variant="default"
								>
									{isJoiningAsSpeaker ? (
										<Loader2 className="mr-1 size-4 animate-spin" />
									) : (
										<Mic className="mr-1 size-4" />
									)}
									Join as Speaker
								</Button>
							)}
							<BroadcastReactions />
							<VolumeControl />
							<ParticipantList />
							<Button
								onClick={handlePopOutToPip}
								size="sm"
								title="Pop out to Picture-in-Picture"
								variant="outline"
							>
								<PictureInPicture2 className="size-4 text-foreground" />
							</Button>
						</div>
					</div>

					<div className="flex-1 overflow-hidden">
						{isConnectingToVoice && (
							<div className="flex h-full flex-col gap-3 p-4">
								<div className="flex h-[50%] flex-col gap-3 rounded-lg border bg-card p-4">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-3">
											<Skeleton className="size-8 rounded-full" />
											<Skeleton className="h-5 w-32" />
										</div>
										<Skeleton className="h-8 w-16" />
									</div>
									<div className="flex-1 rounded-lg bg-muted" />
									<Skeleton className="h-12 w-full" />
								</div>
								<div className="flex h-[50%] flex-col gap-3 rounded-lg border bg-card p-4">
									<div className="flex items-center justify-between">
										<Skeleton className="h-5 w-40" />
										<div className="flex gap-2">
											<Skeleton className="size-8 rounded" />
											<Skeleton className="size-8 rounded" />
										</div>
									</div>
									<div className="flex-1 rounded-lg bg-muted" />
								</div>
							</div>
						)}
						{!isConnectingToVoice && broadcast.status === "paused" && (
							<div className="flex h-full flex-col items-center justify-center gap-4 bg-muted">
								<Radio className="size-16 text-yellow-500" />
								<p className="font-semibold text-foreground text-lg">
									Broadcast Paused
								</p>
								<p className="text-muted-foreground text-sm">
									The broadcaster has paused the stream. Please wait...
								</p>
							</div>
						)}
						{!isConnectingToVoice && broadcast.status !== "paused" && (
							<>
								<BroadcastVideoGrid variant="full" volume={volume} />
								<BroadcastReactionOverlay />
							</>
						)}
					</div>

					{canPublish && <BroadcastSpeakerControlBar />}
				</div>
			</RoomContext.Provider>
		);
	}

	// Combined: voice call + broadcast
	if (hasVoiceCall && hasBroadcast && broadcastRoom && broadcast) {
		const voicePanel = (
			<RoomContext.Provider value={room}>
				<div className="flex h-full flex-col bg-background">
					<BackgroundEffectApplier />
					{isStageChannel ? (
						<StageVideoGrid channelId={activeChannel.id} variant="full" />
					) : (
						<CallVideoGrid variant="full" />
					)}
					<RoomAudioRenderer />
					<DisconnectHandler />
					<ParticipantSoundHandler />
					<RoomOverlay />
					{isStageChannel ? (
						<StageControlBar channelId={activeChannel.id} />
					) : (
						<ControlBar />
					)}
				</div>
			</RoomContext.Provider>
		);

		const broadcastPanel = (
			<RoomContext.Provider value={broadcastRoom}>
				<div className="flex h-full flex-col bg-background">
					<div className="flex-1 overflow-hidden">
						{broadcast.status === "paused" ? (
							<div className="flex h-full flex-col items-center justify-center gap-4 bg-muted">
								<Radio className="size-16 text-yellow-500" />
								<p className="font-semibold text-foreground text-lg">
									Broadcast Paused
								</p>
								<p className="text-muted-foreground text-sm">
									The broadcaster has paused the stream. Please wait...
								</p>
							</div>
						) : (
							<>
								<BroadcastVideoGrid variant="full" volume={volume} />
								<BroadcastReactionOverlay />
							</>
						)}
					</div>
				</div>
			</RoomContext.Provider>
		);

		const layoutDropdown = (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						data-tour="broadcast-orientation"
						size="sm"
						title="Change layout"
						variant="secondary"
					>
						<LayoutDashboard className="size-4 text-foreground" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-48">
					<DropdownMenuLabel>Layout</DropdownMenuLabel>
					<DropdownMenuSeparator />
					{layoutOptions
						.filter((o) => o.group === "single")
						.map((option) => (
							<DropdownMenuItem
								key={option.value}
								onClick={() => setLayoutMode(option.value)}
							>
								<option.icon className="mr-2 size-4" />
								{option.label}
								{layoutMode === option.value && (
									<span className="ml-auto text-primary">✓</span>
								)}
							</DropdownMenuItem>
						))}

					{layoutOptions
						.filter((o) => o.group === "split")
						.map((option) => (
							<DropdownMenuItem
								key={option.value}
								onClick={() => setLayoutMode(option.value)}
							>
								<option.icon className="mr-2 size-4" />
								{option.label}
								{layoutMode === option.value && (
									<span className="ml-auto text-primary">✓</span>
								)}
							</DropdownMenuItem>
						))}
					{layoutOptions
						.filter((o) => o.group === "spotlight")
						.map((option) => (
							<DropdownMenuItem
								key={option.value}
								onClick={() => setLayoutMode(option.value)}
							>
								<option.icon className="mr-2 size-4" />
								{option.label}
								{layoutMode === option.value && (
									<span className="ml-auto text-primary">✓</span>
								)}
							</DropdownMenuItem>
						))}
				</DropdownMenuContent>
			</DropdownMenu>
		);

		const combinedHeader = (
			<div className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-4 max-md:px-14">
				{/* Voice / Broadcast tabs shown only in tabs layout mode */}
				<div className="flex items-center gap-1">
					{layoutMode === "tabs" && (
						<div className="flex items-center gap-1" data-tour="broadcast-tabs">
							<button
								className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium text-sm transition-colors ${
									activeTab === "voice"
										? "bg-primary/10 text-primary"
										: "text-muted-foreground hover:bg-muted hover:text-foreground"
								}`}
								onClick={() => setActiveTab("voice")}
								type="button"
							>
								<Video className="size-3.5" />
								{activeChannel?.name ?? "Video"}
							</button>
							<button
								className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium text-sm transition-all ${
									activeTab === "broadcast"
										? "bg-primary/10 text-primary"
										: "text-muted-foreground hover:bg-muted hover:text-foreground"
								} ${activeTab !== "broadcast" && isBroadcastAudioActive ? "!text-green-600 bg-green-500/20 shadow-[0_0_6px_rgba(34,197,94,0.4)]" : ""}`}
								onClick={() => setActiveTab("broadcast")}
								type="button"
							>
								<Radio
									className={`size-3.5 ${isBroadcastAudioActive && activeTab !== "broadcast" ? "animate-pulse text-green-500" : ""}`}
								/>
								Broadcast
							</button>
							{activeTab === "broadcast" ? (
								<>
									<RoomContext.Provider value={broadcastRoom}>
										<BroadcastReactions />
									</RoomContext.Provider>
									<VolumeControl />
								</>
							) : null}
						</div>
					)}
				</div>
				<div className="flex items-center gap-2">
					{isStageChannel && isStageModerator && (
						<StageRequestQueue channelId={activeChannel.id} />
					)}
					{isInvitedSpeaker && !canPublish && (
						<Button
							disabled={isJoiningAsSpeaker}
							onClick={handleJoinAsSpeaker}
							size="sm"
							variant="default"
						>
							{isJoiningAsSpeaker ? (
								<Loader2 className="mr-1 size-4 animate-spin" />
							) : (
								<Mic className="mr-1 size-4" />
							)}
							Join as Speaker
						</Button>
					)}
					{layoutMode !== "tabs" && layoutMode !== "spotlight-voice" && (
						<>
							<RoomContext.Provider value={broadcastRoom}>
								<BroadcastReactions />
							</RoomContext.Provider>
							<VolumeControl />
						</>
					)}

					{layoutDropdown}
					<Button
						data-tour="pip-button"
						onClick={handlePopOutToPip}
						size="sm"
						title="Pop out to Picture-in-Picture"
						variant="secondary"
					>
						<PictureInPicture2 className="size-4 text-foreground" />
					</Button>
					<RoomContext.Provider
						value={
							layoutMode === "tabs" && activeTab === "voice"
								? room
								: broadcastRoom
						}
					>
						<ParticipantList />
					</RoomContext.Provider>
				</div>
			</div>
		);

		// Split layouts (vertical / horizontal)
		if (layoutMode === "split-vertical" || layoutMode === "split-horizontal") {
			const direction =
				layoutMode === "split-vertical" ? "vertical" : "horizontal";
			return (
				<div className="flex h-full flex-col" data-lk-theme="default">
					{combinedHeader}
					<div className="flex-1 overflow-hidden">
						<ResizablePanelGroup direction={direction}>
							<ResizablePanel defaultSize={50} minSize={20}>
								{voicePanel}
							</ResizablePanel>
							<ResizableHandle //data-tour="broadcast-divider"
								withHandle
							/>
							<ResizablePanel defaultSize={50} minSize={20}>
								{broadcastPanel}
							</ResizablePanel>
						</ResizablePanelGroup>
					</div>
					<BroadcastTour />
				</div>
			);
		}

		// Tabs layout
		if (layoutMode === "tabs") {
			return (
				<div
					className="flex h-full flex-col"
					data-lk-theme="default"
					data-tour="broadcast-layout"
				>
					{combinedHeader}
					<div className="flex-1 overflow-hidden">
						{activeTab === "voice" ? voicePanel : broadcastPanel}
					</div>
					<BroadcastTour />
				</div>
			);
		}

		// Spotlight Voice: voice full-screen, broadcast in small overlay
		if (layoutMode === "spotlight-voice") {
			return (
				<div className="flex h-full flex-col" data-lk-theme="default">
					{combinedHeader}
					<div className="relative flex-1 overflow-hidden">
						{voicePanel}
						<SpotlightOverlay
							header={
								<div className="flex items-center justify-between">
									<div className="flex min-w-0 items-center gap-1.5">
										<Radio className="size-3.5 shrink-0 text-green-500" />
										<span className="truncate font-medium text-foreground text-xs">
											{broadcast.title}
										</span>
									</div>
									<RoomContext.Provider value={broadcastRoom}>
										<BroadcastReactions />
									</RoomContext.Provider>
									<VolumeControl />
								</div>
							}
							label="Broadcast"
						>
							{broadcastPanel}
						</SpotlightOverlay>
					</div>
					<BroadcastTour />
				</div>
			);
		}

		// Spotlight Broadcast: broadcast full-screen, voice in small overlay
		if (layoutMode === "spotlight-broadcast") {
			return (
				<div className="flex h-full flex-col" data-lk-theme="default">
					{combinedHeader}
					<div className="relative flex-1 overflow-hidden">
						{broadcastPanel}
						<SpotlightOverlay label="Video">{voicePanel}</SpotlightOverlay>
					</div>
					<BroadcastTour />
				</div>
			);
		}
	}

	// Fallback: nothing active
	return null;
}

function DisconnectHandler() {
	const room = useRoomContext();
	const handleLeaveChannel = useStore(
		uiStore,
		(state) => state.handleLeaveChannel
	);

	useEffect(() => {
		if (!room) return;

		const handleDisconnected = () => {
			handleLeaveChannel();
		};

		room.on("disconnected", handleDisconnected);

		return () => {
			room.off("disconnected", handleDisconnected);
		};
	}, [room, handleLeaveChannel]);

	return null;
}

function ParticipantSoundHandler() {
	useParticipantSounds();
	return null;
}
